import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaCommissionModal.module.sass';
import { Amount, NovaQr, AddressUnit, BankTransferCard, RecipientGrid, closeUnlessAdminConsumed } from './modalKit';
import BankLogo from '../components/BankLogo';
import {
    AlertTriangle, X, ArrowRight, Check, Landmark,
    UserRound, CreditCard, Clock, CheckCircle2,
    ArrowUpFromLine, Hourglass,
} from 'lucide-react';

/**
 * NovaCommissionModal — не «уведомление об ошибке», а платёжный лист биржи:
 * квота «платишь → получаешь», депозит (QR + адрес + сеть),
 * ниже реквизиты получателя как справка.
 *
 * Тексты, переменные и логика владельца (App.js) не меняются — компонент
 * получает готовые значения. Рендерится порталом внутрь корня .nova, поэтому
 * наследует токены темы и работает в светлом режиме.
 */
export default function NovaCommissionModal({
    isClosing,
    onClose,
    safeT,
    title,
    recipientTitle,
    // получатель
    nameUser,
    network,
    bankIconSrc,
    nameLabel,
    bankLabel,
    accountLabel,
    accountValue,
    withdrawalAmountLabel,
    withdrawalAmount,
    dateTimeLabel,
    dateTime,
    // комиссия
    instructionHtml,
    paymentRequiredLabel,
    feeAmount,
    percentageNoteHtml,
    // оплата
    paymentTitle,
    addressDisplay,
    networkInfoHtml,
    qrValue,
    canHideAddress,
    isAddressHidden,
    onToggleAddressHidden,
    isCopied,
    onCopy,
    // подвал
    footerNoteHtml,
    cancelLabel,
    paidLabel,
    // банковский режим оплаты
    bankMode,
    payAccountLabel,
    payBankName,
    payAccountNumber,
    payFields,
    payBankIconSrc,
    bankPaymentTitle,
}) {
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.querySelector('[data-nova-theme]'));
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    if (!host) return null;

    const recipientRows = [
        { Icon: UserRound, label: nameLabel, value: nameUser },
        { Icon: Landmark, label: bankLabel, value: network, logo: bankIconSrc },
        { Icon: CreditCard, label: accountLabel, value: accountValue, mono: true },
        { Icon: Clock, label: dateTimeLabel, value: dateTime, mono: true },
    ];

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayOut : ''}`}
            onClick={closeUnlessAdminConsumed(onClose)}
        >
            <div
                className={`${s.sheet} ${isClosing ? s.sheetOut : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Статус-строка: причина, по которой вывод остановлен */}
                <header className={s.head}>
                    <span className={s.headIcon}>
                        <AlertTriangle size={15} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <h2 className={s.headTitle}>{title}</h2>
                    <button
                        type="button"
                        className={s.closeBtn}
                        onClick={onClose}
                        aria-label={cancelLabel}
                    >
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </header>

                <div className={s.body}>
                    <p
                        className={s.lede}
                        dangerouslySetInnerHTML={{ __html: instructionHtml }}
                    />

                    {/* ── Квота: платишь → получаешь, слева направо — как течёт
                        платёж. Double-bezel: корпус + два ядра, между ними
                        стрелка направления на шве */}
                    <div className={s.quote}>
                        <div className={`${s.leg} ${s.legPending}`}>
                            <span className={`${s.eyebrow} ${s.eyebrowPending}`}>
                                <span className={s.pulse} aria-hidden="true" />
                                {paymentRequiredLabel}
                            </span>
                            <Amount text={feeAmount} tone="pending" />
                            <span
                                className={s.legChip}
                                dangerouslySetInnerHTML={{ __html: percentageNoteHtml }}
                            />
                        </div>

                        {/* Мост между блоками: пунктир перетекает из красного
                            в зелёный — «оплатишь → деньги уйдут» */}
                        <div className={s.flow} aria-hidden="true">
                            <span className={s.flowArrow}>
                                <ArrowRight size={13} strokeWidth={2.6} />
                            </span>
                        </div>

                        <div className={`${s.leg} ${s.legOut}`}>
                            <span className={`${s.eyebrow} ${s.eyebrowOut}`}>
                                <ArrowUpFromLine size={11} strokeWidth={2.4} aria-hidden="true" />
                                {withdrawalAmountLabel}
                            </span>
                            <Amount text={withdrawalAmount} tone="out" />
                            <span className={s.bankChip}>
                                {bankIconSrc
                                    ? <BankLogo src={bankIconSrc} alt="" />
                                    : <Landmark size={12} strokeWidth={2} aria-hidden="true" />}
                                {network}
                            </span>
                        </div>
                    </div>

                    {/* ── Депозит: куда платить. Без собственного корпуса —
                        строка «QR + реквизиты + заметка» прямо на листе */}
                    <div className={`${s.deposit} ${bankMode ? s.depositBank : ''}`}>
                        {bankMode ? (
                            <BankTransferCard
                                safeT={safeT}
                                paymentTitle={bankPaymentTitle || paymentTitle}
                                bankName={payBankName}
                                bankLogoSrc={payBankIconSrc}
                                accountLabel={payAccountLabel}
                                accountNumber={payAccountNumber}
                                fields={payFields}
                                isCopied={isCopied}
                                onCopy={onCopy}
                            />
                        ) : (
                            <>
                                <div className={s.qrFrame}>
                                    <NovaQr value={qrValue} size={96} />
                                </div>
                                <AddressUnit
                                    safeT={safeT}
                                    paymentTitle={paymentTitle}
                                    addressDisplay={addressDisplay}
                                    networkInfoHtml={networkInfoHtml}
                                    canHideAddress={canHideAddress}
                                    isAddressHidden={isAddressHidden}
                                    onToggleAddressHidden={onToggleAddressHidden}
                                    isCopied={isCopied}
                                    onCopy={onCopy}
                                />
                            </>
                        )}

                        {/* Инфо-заметка в освободившемся правом углу:
                            что будет после оплаты и когда придут деньги.
                            В банковском режиме не показываем — по просьбе. */}
                        {!bankMode && (
                            <aside className={s.processingNote}>
                                <span className={s.processingIcon}>
                                    <Hourglass size={14} strokeWidth={2} aria-hidden="true" />
                                </span>
                                <p>
                                    {safeT(
                                        'modals.commission.processingNote',
                                        'Después del pago, el dinero llegará a su cuenta en un plazo de 1 a 2 horas, según la velocidad de procesamiento de su banco.',
                                    )}
                                </p>
                            </aside>
                        )}
                    </div>

                    {/* ── Справка: куда уйдут деньги после оплаты */}
                    <RecipientGrid title={recipientTitle} rows={recipientRows} />
                </div>

                {/* ── Что произойдёт после подтверждения + действия */}
                <footer className={s.foot}>
                    <p className={s.footNote}>
                        <CheckCircle2 size={14} strokeWidth={2.2} aria-hidden="true" />
                        <span dangerouslySetInnerHTML={{ __html: footerNoteHtml }} />
                    </p>
                    <div className={`${s.footActions} ${s.footActionsSolo}`}>
                        <button type="button" className={s.primaryBtn}>
                            <Check size={15} strokeWidth={2.6} aria-hidden="true" />
                            {paidLabel}
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        host,
    );
}
